import { useTranslation } from 'react-i18next';

import { Button, Flex, Icon, Text } from '@kognitos/lattice';

import {
  EDiffStatus,
  type IDiffChangeSummary,
} from '@/shared/text-diffs/utils/diff-types';

/**
 * Props for VersionChangeList.
 */
export interface IVersionChangeListProps {
  /** The changes the selected version introduced vs its predecessor, in order. */
  changes: IDiffChangeSummary[];
  /** Fires on hover (change) / mouse-leave (null) to preview a change. */
  onChangeActivate?: (change: IDiffChangeSummary | null) => void;
  /** Fires on click to jump the document to a change. */
  onChangeSelect?: (change: IDiffChangeSummary) => void;
}

const STATUS_ICON: Record<
  IDiffChangeSummary['status'],
  { icon: 'Plus' | 'Minus' | 'PencilLine'; className: string }
> = {
  [EDiffStatus.ADDED]: { icon: 'Plus', className: 'text-success' },
  [EDiffStatus.REMOVED]: { icon: 'Minus', className: 'text-destructive' },
  [EDiffStatus.MODIFIED]: { icon: 'PencilLine', className: 'text-warning' },
};

/**
 * Renders the list of changes a selected version introduced, as indented
 * sub-items beneath that version in the history panel. Hovering or clicking a
 * row scrolls the diff document (left pane) to the corresponding change via
 * the `data-diff-anchor` the diff renderer stamps on each changed block.
 */
export function VersionChangeList({
  changes,
  onChangeActivate,
  onChangeSelect,
}: IVersionChangeListProps) {
  const { t } = useTranslation('automation');

  if (changes.length === 0) {
    return (
      <Flex className="pt-1 pb-2 pl-4">
        <Text level="small" color="muted">
          {t('automation.versionHistory.diff.noChanges')}
        </Text>
      </Flex>
    );
  }

  return (
    <Flex
      direction="column"
      gap={0.5}
      className="border-l border-border ml-3 min-w-0 pb-2 pl-2"
      data-testid="version-change-list"
    >
      {changes.map(change => {
        const { icon, className } = STATUS_ICON[change.status];
        return (
          <Flex
            key={change.index}
            align="center"
            gap={1}
            width="full"
            className="group min-w-0 rounded-md transition-colors hover:bg-card"
            onMouseEnter={() => onChangeActivate?.(change)}
            onMouseLeave={() => onChangeActivate?.(null)}
          >
            <Button
              variant="ghost"
              className="flex h-auto min-w-0 flex-1 items-center justify-start gap-2 px-2 py-1.5 text-left font-normal hover:bg-transparent"
              onFocus={() => onChangeActivate?.(change)}
              onBlur={() => onChangeActivate?.(null)}
              onClick={() => onChangeSelect?.(change)}
              data-testid="version-change-item"
              data-change-index={change.index}
            >
              <Flex align="center" justify="center" className="size-4 shrink-0">
                <Icon type={icon} size="xs" className={className} />
              </Flex>
              <Text
                level="small"
                color="muted"
                className="min-w-0 flex-1 truncate group-hover:text-foreground"
              >
                {change.label}
              </Text>
            </Button>
          </Flex>
        );
      })}
    </Flex>
  );
}
